import { useCallback } from "react";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { useRouteParams } from "@repo/core/hooks/useRouteParams";

/** Parses route/search params that may be boolean or string (Expo Router). */
export const parseRouteFlag = (value: unknown): boolean =>
  value === true || value === "true";

/**
 * Navigation helpers for credit/fidelity effectuation flows.
 * Uses `push` to preserve stack so the user can go back and edit previous steps.
 */
export const useProposalFlowNavigation = () => {
  const navigation = useNavigation();
  const routeParams = useRouteParams() as Record<string, unknown> | undefined;

  const isReviewEdit = parseRouteFlag(
    routeParams?.isReviewEdit ?? navigation.params?.isReviewEdit
  );

  const goToNextStep = useCallback(
    (path: string, state?: object) => {
      navigation.push(path, state);
    },
    [navigation]
  );

  const completeStep = useCallback(
    (nextPath: string, state?: object) => {
      if (isReviewEdit) {
        navigation.back();
        return;
      }
      navigation.push(nextPath, state);
    },
    [isReviewEdit, navigation]
  );

  const goToPreviousStep = useCallback(
    (fallbackPath: string, fallbackState?: object) => {
      if (navigation.canGoBack?.()) {
        navigation.back();
        return;
      }
      navigation.replace(fallbackPath, fallbackState);
    },
    [navigation]
  );

  const openReviewEdit = useCallback(
    (path: string, state?: object) => {
      navigation.push(path, { ...state, isReviewEdit: true });
    },
    [navigation]
  );

  return {
    ...navigation,
    routeParams,
    isReviewEdit,
    goToNextStep,
    completeStep,
    goToPreviousStep,
    openReviewEdit
  };
};
